import React, { useState } from "react";
import { 
	Box, 
	Button, 
	Typography, 
	Table, 
	TableBody, 
	TableCell, 
	TableContainer, 
	TableHead, 
	TableRow, 
	Paper, 
	Stack,
	Grid,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	TextField,
	Card,
	CardContent,
	CircularProgress,
	Alert
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import { useGetPropertyReports, useRentals } from "services/hooks/rentals";
import { useSelector } from "react-redux";
import { currencyFormatter } from "utilities/currencyFormatter";

const months = [
	{ value: "", label: "All Months" },
	{ value: "January", label: "January" },
	{ value: "February", label: "February" },
	{ value: "March", label: "March" },
	{ value: "April", label: "April" },
	{ value: "May", label: "May" },
	{ value: "June", label: "June" },
	{ value: "July", label: "July" },
	{ value: "August", label: "August" },
	{ value: "September", label: "September" },
	{ value: "October", label: "October" },
	{ value: "November", label: "November" },
	{ value: "December", label: "December" },
];

const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

const headers = [
	{ label: "Property Name", key: "NAME" },
	{ label: "Unit Number", key: "UNIT_NO" },
	{ label: "Contact/Tenant", key: "CONTACT" },
	{ label: "Rent Amount", key: "RENT_AMOUNT" },
	{ label: "Paid Amount", key: "PAID" },
	{ label: "Balance", key: "BAL" },
	{ label: "Due Date", key: "DUE_DATE" },
	{ label: "Payment Date", key: "PAYT_DATE" },
	{ label: "Payment Method", key: "PAYT_MD" },
	{ label: "Comments", key: "COMMT" },
];

function downloadCSV(data, filename = "property_report.csv") {
	const csvRows = [];
	// Add headers
	csvRows.push(headers.map(h => `"${h.label}"`).join(","));
	// Add data
	data.forEach(row => {
		csvRows.push(headers.map(h => `"${row[h.key] || ''}"`).join(","));
	});
	const csvContent = csvRows.join("\n");
	const blob = new Blob([csvContent], { type: "text/csv" });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	a.click();
	URL.revokeObjectURL(url);
}

function downloadExcel(data, filename = "property_report.xlsx") {
	// Create a new array with the correct structure for Excel
	const excelData = data.map(row => {
		const newRow = {};
		headers.forEach(header => {
			newRow[header.label] = row[header.key];
		});
		return newRow;
	});

	const worksheet = XLSX.utils.json_to_sheet(excelData);
	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, worksheet, "Property Report");
	XLSX.writeFile(workbook, filename);
}

const PropertyReports = () => {
	const landlordId = useSelector((state) => state.landlord);
	const { rentals, isLoadingRentals } = useRentals();
	
	const [filters, setFilters] = useState({
		rental_id: "",
		month: "",
		year: new Date().getFullYear(),
		date: ""
	});

	const { reportData, isLoadingReportData, errorReportData } = useGetPropertyReports(filters);

	const handleFilterChange = (field, value) => {
		setFilters(prev => ({
			...prev,
			[field]: value
		}));
	};

	const handleGenerateReport = () => {
		// The report will be generated automatically when filters change
		// This function can be used for additional validation if needed
	};

	const getReportTitle = () => {
		let title = "Property Report";
		if (filters.rental_id) {
			const selectedProperty = rentals.find(r => r.id === filters.rental_id);
			title = `${selectedProperty?.rental_name || selectedProperty?.name || 'Property'} Report`;
		}
		if (filters.month && filters.year) {
			title += ` - ${filters.month} ${filters.year}`;
		}
		return title;
	};

	return (
		<Box>
			<Typography variant="h4" mb={3} fontWeight="bold">
				{getReportTitle()}
			</Typography>

			{/* Filters Section */}
			<Paper sx={{ p: 3, mb: 3 }}>
				<Typography variant="h6" mb={2}>Report Filters</Typography>
				<Grid container spacing={2} alignItems="center">
					<Grid item xs={12} md={3}>
						<FormControl fullWidth size="small">
							<InputLabel>Property</InputLabel>
							<Select
								value={filters.rental_id}
								label="Property"
								onChange={(e) => handleFilterChange('rental_id', e.target.value)}
								disabled={isLoadingRentals}
							>
								<MenuItem value="">All Properties</MenuItem>
								{rentals && rentals.length > 0 ? (
									rentals.map((rental) => (
										<MenuItem key={rental.id} value={rental.id}>
											{rental.rental_name || rental.name || `Property ${rental.id}`}
										</MenuItem>
									))
								) : (
									!isLoadingRentals && (
										<MenuItem disabled>No properties available</MenuItem>
									)
								)}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={12} md={2}>
						<FormControl fullWidth size="small">
							<InputLabel>Month</InputLabel>
							<Select
								value={filters.month}
								label="Month"
								onChange={(e) => handleFilterChange('month', e.target.value)}
							>
								{months.map((month) => (
									<MenuItem key={month.value} value={month.value}>
										{month.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={12} md={2}>
						<FormControl fullWidth size="small">
							<InputLabel>Year</InputLabel>
							<Select
								value={filters.year}
								label="Year"
								onChange={(e) => handleFilterChange('year', e.target.value)}
							>
								{years.map((year) => (
									<MenuItem key={year} value={year}>
										{year}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={12} md={3}>
						<TextField
							fullWidth
							size="small"
							type="date"
							label="Specific Date (Optional)"
							value={filters.date}
							onChange={(e) => handleFilterChange('date', e.target.value)}
							InputLabelProps={{ shrink: true }}
						/>
					</Grid>
					<Grid item xs={12} md={2}>
						<Button
							variant="contained"
							onClick={handleGenerateReport}
							fullWidth
						>
							Generate Report
						</Button>
					</Grid>
				</Grid>
			</Paper>

			{/* Summary Cards */}
			{!isLoadingReportData && !errorReportData && reportData.summary && (
				<Grid container spacing={2} mb={3}>
					<Grid item xs={12} md={2}>
						<Card>
							<CardContent>
								<Typography variant="h6" color="primary">
									{reportData.summary.total_units}
								</Typography>
								<Typography variant="body2">Total Units</Typography>
							</CardContent>
						</Card>
					</Grid>
					<Grid item xs={12} md={2}>
						<Card>
							<CardContent>
								<Typography variant="h6" color="success.main">
									{currencyFormatter(reportData.summary.total_rent_amount)}
								</Typography>
								<Typography variant="body2">Total Rent Amount</Typography>
							</CardContent>
						</Card>
					</Grid>
					<Grid item xs={12} md={2}>
						<Card>
							<CardContent>
								<Typography variant="h6" color="info.main">
									{currencyFormatter(reportData.summary.total_paid_amount)}
								</Typography>
								<Typography variant="body2">Total Paid Amount</Typography>
							</CardContent>
						</Card>
					</Grid>
					<Grid item xs={12} md={2}>
						<Card>
							<CardContent>
								<Typography variant="h6" color="error">
									{currencyFormatter(reportData.summary.total_balance)}
								</Typography>
								<Typography variant="body2">Total Balance</Typography>
							</CardContent>
						</Card>
					</Grid>
					<Grid item xs={12} md={2}>
						<Card>
							<CardContent>
								<Typography variant="h6" color="success.main">
									{reportData.summary.occupied_units}
								</Typography>
								<Typography variant="body2">Occupied Units</Typography>
							</CardContent>
						</Card>
					</Grid>
					<Grid item xs={12} md={2}>
						<Card>
							<CardContent>
								<Typography variant="h6" color="warning.main">
									{reportData.summary.unoccupied_units}
								</Typography>
								<Typography variant="body2">Unoccupied Units</Typography>
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			)}

			{/* Download Buttons */}
			{!isLoadingReportData && !errorReportData && reportData.report_data.length > 0 && (
				<Stack direction="row" spacing={2} mb={3}>
					<Button
						variant="contained"
						color="primary"
						startIcon={<DownloadIcon />}
						onClick={() => downloadCSV(reportData.report_data, `${getReportTitle()}.csv`)}
					>
						Download CSV
					</Button>
					<Button
						variant="contained"
						color="success"
						startIcon={<DownloadIcon />}
						onClick={() => downloadExcel(reportData.report_data, `${getReportTitle()}.xlsx`)}
					>
						Download Excel
					</Button>
				</Stack>
			)}

			{/* Loading State */}
			{isLoadingReportData && (
				<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
					<CircularProgress />
				</Box>
			)}

			{/* Error State */}
			{errorReportData && (
				<Alert severity="error" sx={{ mb: 3 }}>
					Error loading report data. Please try again.
				</Alert>
			)}

			{/* Report Table */}
			{!isLoadingReportData && !errorReportData && (
				<TableContainer component={Paper}>
					<Table>
						<TableHead>
							<TableRow>
								{headers.map(h => (
									<TableCell key={h.key} sx={{ fontWeight: 'bold' }}>
										{h.label}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{reportData.report_data.length > 0 ? (
								reportData.report_data.map((row, idx) => (
									<TableRow key={idx}>
										<TableCell>{row.NAME}</TableCell>
										<TableCell>{row.UNIT_NO}</TableCell>
										<TableCell>{row.CONTACT}</TableCell>
										<TableCell>{currencyFormatter(row.RENT_AMOUNT)}</TableCell>
										<TableCell>{currencyFormatter(row.PAID)}</TableCell>
										<TableCell>
											<Typography 
												color={row.BAL > 0 ? "error" : "success.main"}
												fontWeight="bold"
											>
												{currencyFormatter(row.BAL)}
											</Typography>
										</TableCell>
										<TableCell>{row.DUE_DATE}</TableCell>
										<TableCell>{row.PAYT_DATE || '-'}</TableCell>
										<TableCell>{row.PAYT_MD || '-'}</TableCell>
										<TableCell>{row.COMMT}</TableCell>
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell colSpan={headers.length} align="center">
										<Typography variant="body1" color="text.secondary">
											No data available for the selected filters
										</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Box>
	);
};

export default PropertyReports; 